import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import { Lang, Std, ITranslationDatabase } from "../sharedTypes";
import { } from "../sharedTypes";
import { ng } from "@common/ngds";
import { Placement } from "react-bootstrap/esm/types";
import { ILangItem, IReportedDesignChange, IUserQuantitySettings, IUserSettings } from "./../sharedTypes";
import { JSX } from "react";
import { useAppSelector } from "../app/hooks";

export type Settings = IUserSettings;

/**
  if url is missing, no link is shown
  if url is present, a link is shown and urlText is required
  label is a UNSAFE html string
*/
// todo(Gustav): make more typesafe
// todo(Gustav): make label more html safe
export const NGOverlay = (props: {
  desc: string;
  url?: string | undefined;
  urlText?: string | JSX.Element;
  label: string;
  placement: Placement;
}) => {
  const renderTooltip = (tprops: any) =>
    props.url !== undefined && props.url !== null ? (
      <Tooltip id="button-tooltip" {...tprops}>
        {props.desc}
        <br></br>
        <a href={props.url} target={"_blank"} rel="noreferrer">
          {props.urlText}
        </a>
      </Tooltip>
    ) : (
      <Tooltip id="button-tooltip" {...tprops}>
        {props.desc}
      </Tooltip>
    );

  return (
    <OverlayTrigger
      placement={props.placement}
      delay={{ show: 250, hide: props.url !== undefined ? 2000 : 400 }}
      overlay={renderTooltip}
    >
      <span
        // todo(Gustav): variant property doesn't exist on span... remove or fix?
        // variant="success"
        style={{ textTransform: "none" }}
        dangerouslySetInnerHTML={{ __html: props.label }}
      ></span>
    </OverlayTrigger>
  );
};

export interface FakeEvent {
  target: {
    value: string;

    selectedIndex?: number;
    options?: {
      text: string;
    }[];
  };
}

export function getLabel(db: ITranslationDatabase, lang: Lang, tag: string): string;
export function getLabel(db: ITranslationDatabase, lang: Lang, tag: string, bDesc: boolean): string | JSX.Element;
export function getLabel(
  db: ITranslationDatabase,
  lang: Lang,
  tag: string,
  bDesc: boolean,
  showUrl: boolean,
): string | JSX.Element;
export function getLabel(
  db: ITranslationDatabase,
  lang: Lang,
  tag: string,
  bDesc: boolean,
  showUrl: boolean,
  placement: Placement,
): string | JSX.Element;
export function getLabel(
  db: ITranslationDatabase,
  lang: Lang,
  tag: string,
  bDesc = false,
  showUrl = false,
  placement = "right" as Placement,
): string | JSX.Element {
  if (db === null || db === undefined) return tag;

  const dbTag = db[tag];
  if (dbTag === undefined) return tag;

  const dbLabel = dbTag.label;
  if (dbLabel === undefined) return tag;

  let label = dbLabel[lang as keyof ILangItem] ?? dbLabel.en;

  if (label === "?") {
    const symbol = dbTag.symbol?.SI ?? null;
    if (typeof symbol === "string") label = symbol;
  }

  if (!bDesc) return label;

  let desc = "";
  if (dbTag.description) {
    desc = dbTag.description[lang as keyof ILangItem] ?? dbTag.description.en;
  }
  if (desc === "") return label;

  // if (showUrl) {
  //   let slug = "";
  //   if (dbTag.slug) {
  //     slug = dbTag.slug[lang] ?? dbTag.slug.en;
  //   }

  //   let text = getLabel(db, lang, "go_to_documentation_");
  //   let url = config.APP_DOCUMENTATION_URL + slug;
  //   return (
  //     <NGOverlay
  //       desc={desc}
  //       label={label}
  //       url={url}
  //       urlText={text}
  //     ></NGOverlay>
  //   );
  // } else {
  //   return <NGOverlay desc={desc} label={label}></NGOverlay>;
  // }

  return <NGOverlay desc={desc} label={label} placement={placement}></NGOverlay>;
}

// Currently defined as (non breaking) white space, but not using &nbsp; since that caused issues when using it in react
// https://stackoverflow.com/questions/37909134/nbsp-jsx-not-working
export const DISTANCE_LABEL = "\u00A0";

// This is a mix of getLabel and getUnitLabel.
// It returns the text associated with the label tag, and with the unit added in brackets.
// If bDesc is true, the description is shown as a tooltip where the entire label (inc. unit) is overlayed.
export function getLabelWithUnit(
  db: ITranslationDatabase,
  settings: Settings,
  tag: string,
  bDesc = false,
): string | JSX.Element {
  if (db === null || db === undefined) return tag;

  const lang = settings.lang as Lang;
  const dbTag = db[tag];
  if (dbTag === undefined) return tag;

  const dbLabel = dbTag.label;
  if (dbLabel === undefined) return tag;

  let label = dbLabel[lang as keyof ILangItem] ?? dbLabel.en;

  if (label === "?") {
    const symbol = dbTag.symbol?.SI ?? null;
    if (typeof symbol === "string") label = symbol;
  }

  const unit_label = getUnitLabel({ db, tag: tag, settings, style: "string" }).toString();
  label = label.concat(DISTANCE_LABEL, "[", unit_label, "]");

  if (!bDesc) return label;

  let desc = "";
  if (dbTag.description) {
    desc = dbTag.description[lang as keyof ILangItem] ?? dbTag.description.en;
  }
  if (desc === "") return label;

  return <NGOverlay desc={desc} label={label} placement={"right" as Placement}></NGOverlay>;
}

export const getText = (db: ITranslationDatabase | null | undefined, lang: Lang, tag: string): string | undefined => {
  if (db === null || db === undefined) {
    return tag;
  }
  const dbTag = db[tag];

  if (!dbTag) {
    return undefined;
  }

  const dbLabel = dbTag.label;
  if (!dbLabel) {
    return undefined;
  }

  let text = dbLabel.en;
  const dbLabelLang = dbLabel[lang as keyof ILangItem];
  if (dbLabelLang) {
    text = dbLabelLang;
  }

  text = text.charAt(0).toLowerCase() + text.slice(1);
  return text;
};

export const getDescription = (db: ITranslationDatabase, lang: Lang, tag: string): string => {
  const dbTag = db[tag];
  if (dbTag && dbTag.description) {
    return dbTag.description[lang as keyof ILangItem] ?? dbTag.description.en;
  }
  return "-";
};

export const getSymbolLabel = (props: { db: ITranslationDatabase | null | undefined; std: Std; tag: string }) => {
  let db = props.db;
  let std = props.std;
  let tag = props.tag;

  if (!db) return undefined;

  const dbTag = db[tag];
  if (!dbTag) return undefined;
  if (!dbTag.symbol) return undefined;

  let html_symbol = dbTag.symbol[std];
  return html_symbol;
};

export const getSymbolLabelAsString = (props: {
  db: ITranslationDatabase | null | undefined;
  std: Std;
  tag: string;
}): string => {
  const r = getSymbolLabel(props);
  if (r === undefined) return "";
  if (typeof r === "string") return r;

  // todo(Gustav): is this correct?
  return r.name;
};

const getQuantity = (settings: IUserSettings, name: string) => {
  const key = name as keyof IUserQuantitySettings;
  const found = settings.quantity[key];
  if (found !== undefined && typeof found !== "string") {
    return found;
  } else {
    return undefined;
  }
};

/**
 *
 * @param db localisation
 * @param tag a string that for example could be "displacement_", "moment_", "normalforce_", "shearforce_",
 * @param settings viewport.context.user.settings
 * @param style string or html
 */
export type LabelType = string | boolean | JSX.Element;
export const getUnitLabel = (props: {
  db: ITranslationDatabase;
  tag: string;
  settings: any;
  style: "string" | "html";
}): LabelType => {
  let db = props.db;
  let tag = props.tag;
  let style = props.style;
  let settings = props.settings;

  const INVALID_VALUE = false;

  if (!db) {
    return INVALID_VALUE;
  }

  const dbTag = db[tag];
  if (!dbTag) {
    return (
      <>
        <var dangerouslySetInnerHTML={{ __html: tag }} />
      </>
    );
  }

  if (dbTag.quantity && settings.quantity) {
    let pseudoEq = dbTag.quantity; // e.g. ["mass", "/", "length", "3"] -->  "kg/m3"
    let unit = "";
    switch (style) {
      case "string":
        for (let tmp of pseudoEq) {
          const found = getQuantity(settings, tmp);
          if (found !== undefined) {
            unit += found.name;
          } else {
            unit += tmp;
          }
        }
        return unit;

      case "html":
        for (let ind = 0; ind < pseudoEq.length; ind++) {
          let tmp = pseudoEq[ind];
          const found = getQuantity(settings, tmp);
          if (found !== undefined) {
            unit += found.name;
          } else {
            if (isNaN(Number(tmp))) {
              unit += tmp;
            } else {
              if (ind === 0) {
                unit += tmp;
              } else {
                if (getQuantity(settings, pseudoEq[ind]) === undefined) {
                  unit += tmp;
                } else {
                  unit += "<sup>" + tmp + "</sup>";
                }
              }
            }
          }
        }

        return (
          <>
            <var dangerouslySetInnerHTML={{ __html: unit }} />
          </>
        );
      default:
        break;
    }
  }
  return INVALID_VALUE;
};

/**
 * @param db localisation
 * @param tag a string that for example could be "displacement_", "moment_", "normalforce_", "shearforce_",
 * @param settings viewport.context.user.settings
 * @returns a value to be multiplied with the SI value to get the display value
 */
export const getScaleFactor = (props: { db: ITranslationDatabase; tag: string; settings: Settings }): number => {
  let db = props.db;
  let tag = props.tag;
  let settings = props.settings;

  const INVALID_SCALE_FACTOR = 1;

  if (!props.db) {
    return INVALID_SCALE_FACTOR;
  }

  const dbTag = db[tag];
  if (!dbTag) {
    return INVALID_SCALE_FACTOR;
  }

  if (!dbTag.quantity) return INVALID_SCALE_FACTOR;
  if (!settings.quantity) return INVALID_SCALE_FACTOR;

  let pseudoEq = dbTag.quantity; // e.g. ["mass", "/", "length", "3"] -->  "kg/m3"
  let div = false;
  let scaleFactor = 1;
  let size = 1;

  for (let tmp of pseudoEq) {
    const found = getQuantity(settings, tmp);
    if (found !== undefined) {
      if (found.size !== undefined) {
        size = found.size;
        if (div) {
          scaleFactor *= 1 / size;
        } else {
          scaleFactor *= size;
        }
      }
    } else if (isNaN(Number(tmp))) {
      if (tmp === "/") {
        div = true;
      }
    } else {
      scaleFactor *= Math.pow(div ? 1 / size : size, Number(tmp) - 1);
    }
  }

  return scaleFactor;
};

/**
 * Converts a value to a display string
 */
export function GetValLabel(val: number): string {
  let tmp = Math.trunc(Math.abs(val));
  let nbrOfInt = 0;

  if (tmp > 0) {
    nbrOfInt = String(tmp).length;
  }

  let prec = 0;

  if (nbrOfInt > 5) {
    return val.toExponential(2);
  } else {
    if (nbrOfInt === 0) {
      prec = 2;
    } else if (nbrOfInt < 3) {
      prec = 3 - nbrOfInt;
    }
    const rounded = Math.round(Number(val) * Math.pow(10, prec)) / Math.pow(10, prec);
    return rounded.toFixed(prec);
  }
}

/**
 * Converts a value to a display string, with provided number of decimnals
 */
export function GetValLabelDec(val: number, decimals: number): string {
  const rounded = Math.round(Number(val) * Math.pow(10, decimals)) / Math.pow(10, decimals);
  return rounded.toFixed(decimals);
}

/** family names corresponds to data structure 'section.proto'
 */
export function GetFamilyName(family: number): string {
  // SECTION_TYPE_UNSPECIFIED = 0;
  // SECTION_TYPE_CUSTOM = 1;    // Custom, uses CustomParams
  // SECTION_TYPE_R = 2;         // Rectangular, uses RParams
  // SECTION_TYPE_SAWN_LUMBER = 3;   // Sawn lumber, uses RParams
  // SECTION_TYPE_DESSED_LUMBER = 4; // Dressed lumber, uses RParams
  // SECTION_TYPE_GLULAM = 5;        // Glulam, uses RParams
  // SECTION_TYPE_VR = 6;            // Variable rectangular, uses VRParams
  // SECTION_TYPE_T = 7;             // T section, uses TParams
  // SECTION_TYPE_TPS = 8;           // TPS, uses TParams
  // SECTION_TYPE_VT = 9;            // Variable T section, uses VTParams
  // SECTION_TYPE_F = 10;            // F section (Up-side-down T), uses FParams
  // SECTION_TYPE_KB = 11;           // KB section with variable flange width, uses KBParams
  // SECTION_TYPE_KBE = 12;          // KBE section with variable flange width, uses KBEParams
  // SECTION_TYPE_C = 13;            // Solid circular, uses CParams
  // SECTION_TYPE_I = 14;            // I section, uses IParams
  // SECTION_TYPE_HEA = 15;          // HE-A, uses IParams
  // SECTION_TYPE_HEB = 16;          // HE-B, uses IParams
  // SECTION_TYPE_HEM = 17;          // HE-M, uses IParams
  // SECTION_TYPE_IPE = 18;          // IPE, uses IParams
  // SECTION_TYPE_UKB = 19;          // UKB, uses IParams
  // SECTION_TYPE_UKC = 20;          // UKC, uses IParams
  // SECTION_TYPE_IV = 21;           // I section with variables flanges, uses IVParams
  // SECTION_TYPE_L = 22;            // L section, uses LParams
  // SECTION_TYPE_LE = 23;           // LE, uses LParams
  // SECTION_TYPE_LU = 24;           // LU, uses LParams
  // SECTION_TYPE_CO = 25;           // Pipe section, uses COParams
  // SECTION_TYPE_CHS = 26;          // CHS, uses COParams
  // SECTION_TYPE_KCKR = 27;         // CHS, uses COParams
  // SECTION_TYPE_VCKR = 28;         // CHS, uses COParams
  // SECTION_TYPE_U = 29;            // U section, uses UParams
  // SECTION_TYPE_UAP = 30;          // UAP, uses UParams
  // SECTION_TYPE_UPE_DIN = 31;      // UPE (DIN 1026-2), uses UParams
  // SECTION_TYPE_UPE_NEN = 32;      // UPE (NEN-EN 10025-1/2), uses UParams
  // SECTION_TYPE_UPE_SWE = 33;      // UPE-Swedish, uses UParams
  // SECTION_TYPE_RHS = 34;          // RHS-section, uses RHSParams
  // SECTION_TYPE_KKR = 35;          // KKR, uses RHSParams
  // SECTION_TYPE_VKR = 36;          // VKR, uses RHSParams
  // SECTION_TYPE_Z = 37;            // Z section, uses ZParams
  // SECTION_TYPE_TOPPING = 38;      // Topping, uses ToppingParams
  // SECTION_TYPE_HDX = 39;          // Hollowcore section, may use HDXParams but are ideally given perimeter/holes polyline(s) directly
  // SECTION_TYPE_UB = 40;           // UB, uses IVParams
  // SECTION_TYPE_UBP = 41;          // UBP, uses IVParams
  // SECTION_TYPE_UC = 42;           // UC, uses IVParams
  // SECTION_TYPE_PFC = 43;          // PFC, uses UParams
  // SECTION_TYPE_EA = 44;           // EA, uses LParams
  // SECTION_TYPE_UA = 45;           // UA, uses LParams
  // SECTION_TYPE_ASB = 46;          // ASB, uses IVParams
  // SECTION_TYPE_KERTO = 47;        // Kerto, uses RParams
  // SECTION_TYPE_PLATE = 48;        // Plate sections, 1m strips with different height, uses RParams
  // SECTION_TYPE_HSQ = 49;           // HSQ, uses HSQParams
  // SECTION_TYPE_UX = 50;           // welded U-section, uses UXParams
  // SECTION_TYPE_ZX = 51;           // welded Z-section, uses ZXParams

  let familyArr = [
    "R",
    "Sawn",
    "Dressed",
    "Glulam",
    "VR",
    "T",
    "TPS",
    "VT",
    "F",
    "KB",
    "KBE",
    "C",
    "I",
    "HEA",
    "HEB",
    "HEM",
    "IPE",
    "UKB",
    "UKC",
    "IV",
    "L",
    "LE",
    "LU",
    "CO",
    "CHS",
    "KCKR",
    "VCKR",
    "U",
    "UAP",
    "UPE_DIN",
    "UPE_NEN",
    "UPE_SWE",
    "RHS",
    "KKR",
    "VKR",
    "Z",
    "Topping",
    "HDX",
    "UB",
    "UBP",
    "UC",
    "PFC",
    "EA",
    "UA",
    "ASB",
    "Kerto",
    "PL",
    "HSQ",
    "UX",
    "ZX",
    "S",
  ];

  return familyArr[family - 2];
}

/** Override function for section famility name, should it differ from the default.
 * Uses i18next t function for translation.
 */
import { t } from "i18next";
import { get } from "http";
import { useSelector } from "react-redux";
export function GetFamilyDisplayName(familyName: string): string {
  switch (familyName) {
    case "R":
      return t("section_type_name_r_.label");
    case "T":
      return t("section_type_name_t_.label");
    case "PL":
      return t("section_type_name_pl_.label");
    default:
      return familyName;
  }
}

/** Returns a localized string for given material and control type (used in code check etc.).
 * @param db localisation database
 * @param lang requested language
 * @param type current control type (string or enum)
 * @param shortName retruns the short version of the name
 */
export const getControlTypeName = (
  mat: ng.material.Mtrl,
  db: any,
  lang: Lang,
  type:
    | string
    | ng.result.control.ControlTypeConcrete
    | ng.result.control.ControlTypeSteel
    | ng.result.control.ControlTypeTimber,
  shortName: boolean = false,
  showDesc = false,
  placement = "right" as Placement,
) => {
  switch (mat) {
    case ng.material.Mtrl.MTRL_CONCRETE:
      return getConcreteControlTypeName(
        db,
        lang,
        type as ng.result.control.ControlTypeConcrete,
        shortName,
        showDesc,
        placement,
      );
    case ng.material.Mtrl.MTRL_STEEL:
      return getSteelControlTypeName(
        db,
        lang,
        type as ng.result.control.ControlTypeSteel,
        shortName,
        showDesc,
        placement,
      );
    case ng.material.Mtrl.MTRL_TIMBER:
      return getTimberControlTypeName(
        db,
        lang,
        type as ng.result.control.ControlTypeTimber,
        shortName,
        showDesc,
        placement,
      );
    case ng.material.Mtrl.MTRL_UNSPECIFIED:
    default:
      return "";
  }
};

/** Returns a localized string for given concrete control type (used in code check etc.).
 * @param db localisation database
 * @param lang requested language
 * @param type current control type (string or enum)
 * @param shortName retruns the short version of the name
 */
export function getConcreteControlTypeName(
  db: any,
  lang: Lang,
  type: string | ng.result.control.ControlTypeConcrete,
): string;
export function getConcreteControlTypeName(
  db: any,
  lang: Lang,
  type: string | ng.result.control.ControlTypeConcrete,
  shortName: boolean,
): string;
export function getConcreteControlTypeName(
  db: any,
  lang: Lang,
  type: string | ng.result.control.ControlTypeConcrete,
  shortName: boolean,
  showDesc: boolean,
  placement: Placement,
): string | JSX.Element;
export function getConcreteControlTypeName(
  db: any,
  lang: Lang,
  type: string | ng.result.control.ControlTypeConcrete,
  shortName = false,
  showDesc = false,
  placement = "right" as Placement,
): string | JSX.Element {
  const getName = (tag: string) => {
    return shortName
      ? getLabel(db, lang, tag + "short_", showDesc, false, placement)
      : getLabel(db, lang, tag, showDesc, false, placement);
  };

  switch (type) {
    default:
    case "CONTROL_TYPE_CONCRETE_UNSPECIFIED":
    case ng.result.control.ControlTypeConcrete.CONTROL_TYPE_CONCRETE_UNSPECIFIED: {
      return getName("control_type_concrete_unspecified_");
    }
    case "CONTROL_TYPE_CONCRETE_POSITIVE_MOMENT_M1":
    case ng.result.control.ControlTypeConcrete.CONTROL_TYPE_CONCRETE_POSITIVE_MOMENT_M1: {
      return getName("control_type_concrete_moment_positive_");
    }
    case "CONTROL_TYPE_CONCRETE_NEGATIVE_MOMENT_M1":
    case ng.result.control.ControlTypeConcrete.CONTROL_TYPE_CONCRETE_NEGATIVE_MOMENT_M1: {
      return getName("control_type_concrete_moment_negative_");
    }
    case "CONTROL_TYPE_CONCRETE_MOMENT_M2":
    case ng.result.control.ControlTypeConcrete.CONTROL_TYPE_CONCRETE_MOMENT_M2: {
      return getName("control_type_concrete_moment_z_");
    }
    case "CONTROL_TYPE_CONCRETE_BIAXIAL_MOMENT":
    case ng.result.control.ControlTypeConcrete.CONTROL_TYPE_CONCRETE_BIAXIAL_MOMENT: {
      return getName("control_type_concrete_moment_biaxial_");
    }
    case "CONTROL_TYPE_CONCRETE_AXIAL_FORCE":
    case ng.result.control.ControlTypeConcrete.CONTROL_TYPE_CONCRETE_AXIAL_FORCE: {
      return getName("control_type_concrete_normal_force_");
    }
    case "CONTROL_TYPE_CONCRETE_SHEAR_FORCE":
    case ng.result.control.ControlTypeConcrete.CONTROL_TYPE_CONCRETE_SHEAR_FORCE: {
      return getName("control_type_concrete_shear_force_");
    }
    case "CONTROL_TYPE_CONCRETE_SHEAR_FORCE_TOPPING":
    case ng.result.control.ControlTypeConcrete.CONTROL_TYPE_CONCRETE_SHEAR_FORCE_TOPPING: {
      return getName("control_type_concrete_shear_topping_");
    }
    case "CONTROL_TYPE_CONCRETE_TOPPING_JOINT":
    case ng.result.control.ControlTypeConcrete.CONTROL_TYPE_CONCRETE_TOPPING_JOINT: {
      return getName("control_type_concrete_topping_joint_");
    }
    case "CONTROL_TYPE_CONCRETE_POSITIVE_CRACK_WIDTH":
    case ng.result.control.ControlTypeConcrete.CONTROL_TYPE_CONCRETE_POSITIVE_CRACK_WIDTH: {
      return getName("control_type_concrete_crack_width_positive_");
    }
    case "CONTROL_TYPE_CONCRETE_NEGATIVE_CRACK_WIDTH":
    case ng.result.control.ControlTypeConcrete.CONTROL_TYPE_CONCRETE_NEGATIVE_CRACK_WIDTH: {
      return getName("control_type_concrete_crack_width_negative_");
    }
    case "CONTROL_TYPE_CONCRETE_COMPRESSION_STRESS":
    case ng.result.control.ControlTypeConcrete.CONTROL_TYPE_CONCRETE_COMPRESSION_STRESS: {
      return getName("control_type_concrete_compression_stress_");
    }
    case "CONTROL_TYPE_CONCRETE_INITIAL_PRESTRESS":
    case ng.result.control.ControlTypeConcrete.CONTROL_TYPE_CONCRETE_INITIAL_PRESTRESS: {
      return getName("control_type_concrete_initial_prestress_");
    }
    case "CONTROL_TYPE_CONCRETE_STRESS_AFTER_RELEASE":
    case ng.result.control.ControlTypeConcrete.CONTROL_TYPE_CONCRETE_STRESS_AFTER_RELEASE: {
      return getName("control_type_concrete_stress_after_release_");
    }
    case "CONTROL_TYPE_CONCRETE_HOLLOWCORE_SPALLING":
    case ng.result.control.ControlTypeConcrete.CONTROL_TYPE_CONCRETE_HOLLOWCORE_SPALLING: {
      return getName("control_type_concrete_hollowcore_spalling_");
    }
    case "CONTROL_TYPE_CONCRETE_TORSION_TRANSVERSE":
    case ng.result.control.ControlTypeConcrete.CONTROL_TYPE_CONCRETE_TORSION_TRANSVERSE: {
      return getName("control_type_concrete_torsion_transverse_");
    }
    case "CONTROL_TYPE_CONCRETE_TORSION_LONGITUDINAL":
    case ng.result.control.ControlTypeConcrete.CONTROL_TYPE_CONCRETE_TORSION_LONGITUDINAL: {
      return getName("control_type_concrete_torsion_longitudinal_");
    }
    case "CONTROL_TYPE_CONCRETE_TORSION_COMPRESSION_TRANSVERSE":
    case ng.result.control.ControlTypeConcrete.CONTROL_TYPE_CONCRETE_TORSION_COMPRESSION_TRANSVERSE: {
      return getName("control_type_concrete_torsion_compression_transverse_");
    }
    case "CONTROL_TYPE_CONCRETE_TORSION_TENSION_TRANSVERSE":
    case ng.result.control.ControlTypeConcrete.CONTROL_TYPE_CONCRETE_TORSION_TENSION_TRANSVERSE: {
      return getName("control_type_concrete_torsion_tension_transverse_");
    }
    case "CONTROL_TYPE_CONCRETE_TORSION_COMPRESSION_LONGITUDINAL":
    case ng.result.control.ControlTypeConcrete.CONTROL_TYPE_CONCRETE_TORSION_COMPRESSION_LONGITUDINAL: {
      return getName("control_type_concrete_torsion_compression_longitudinal_");
    }
    case "CONTROL_TYPE_CONCRETE_COMPRESSION_COVER_CHECK":
    case ng.result.control.ControlTypeConcrete.CONTROL_TYPE_CONCRETE_COMPRESSION_COVER_CHECK: {
      return getName("control_type_concrete_compression_cover_check_");
    }
    case "CONTROL_TYPE_CONCRETE_SHEAR_STIRRUPS":
    case ng.result.control.ControlTypeConcrete.CONTROL_TYPE_CONCRETE_SHEAR_STIRRUPS: {
      return getName("control_type_concrete_shear_stirrups_");
    }
    case "CONTROL_TYPE_CONCRETE_SHEAR_STIRRUPS_TOPPING":
    case ng.result.control.ControlTypeConcrete.CONTROL_TYPE_CONCRETE_SHEAR_STIRRUPS_TOPPING: {
      return getName("control_type_concrete_shear_stirrups_topping_");
    }
    case "CONTROL_TYPE_CONCRETE_DEFLECTION":
    case ng.result.control.ControlTypeConcrete.CONTROL_TYPE_CONCRETE_DEFLECTION: {
      return getName("control_type_concrete_deflection_");
    }
  }
}

/** Returns a localized string for given steel control type (used in code check etc.).
 * @param db localisation database
 * @param lang requested language
 * @param type current control type (string or enum)
 * @param shortName retruns the short version of the name
 */
export function getSteelControlTypeName(db: any, lang: Lang, type: string | ng.result.control.ControlTypeSteel): string;
export function getSteelControlTypeName(
  db: any,
  lang: Lang,
  type: string | ng.result.control.ControlTypeSteel,
  shortName: boolean,
): string;
export function getSteelControlTypeName(
  db: any,
  lang: Lang,
  type: string | ng.result.control.ControlTypeSteel,
  shortName: boolean,
  showDesc: boolean,
  placement: Placement,
): string | JSX.Element;
export function getSteelControlTypeName(
  db: any,
  lang: Lang,
  type: string | ng.result.control.ControlTypeSteel,
  shortName: boolean = false,
  showDesc = false,
  placement = "right" as Placement,
): string | JSX.Element {
  const getName = (tag: string) => {
    return shortName
      ? getLabel(db, lang, tag + "short_", showDesc, false, placement)
      : getLabel(db, lang, tag, showDesc, false, placement);
  };

  switch (type) {
    default:
    case "CONTROL_TYPE_STEEL_UNSPECIFIED":
    case ng.result.control.ControlTypeSteel.CONTROL_TYPE_STEEL_UNSPECIFIED: {
      return getName("control_type_steel_unspecified_");
    }
    case "CONTROL_TYPE_STEEL_OVERALL":
    case ng.result.control.ControlTypeSteel.CONTROL_TYPE_STEEL_OVERALL: {
      return getName("control_type_steel_overall_");
    }
    case "CONTROL_TYPE_STEEL_N":
    case ng.result.control.ControlTypeSteel.CONTROL_TYPE_STEEL_N: {
      return getName("control_type_steel_normal_force_");
    }
    case "CONTROL_TYPE_STEEL_N_FIRE":
    case ng.result.control.ControlTypeSteel.CONTROL_TYPE_STEEL_N_FIRE: {
      return getName("control_type_steel_normal_force_fire_");
    }
    case "CONTROL_TYPE_STEEL_M1":
    case ng.result.control.ControlTypeSteel.CONTROL_TYPE_STEEL_M1: {
      return getName("control_type_steel_moment_strong_");
    }
    case "CONTROL_TYPE_STEEL_M2":
    case ng.result.control.ControlTypeSteel.CONTROL_TYPE_STEEL_M2: {
      return getName("control_type_steel_moment_weak_");
    }
    case "CONTROL_TYPE_STEEL_M1_FIRE":
    case ng.result.control.ControlTypeSteel.CONTROL_TYPE_STEEL_M1_FIRE: {
      return getName("control_type_steel_moment_strong_fire_");
    }
    case "CONTROL_TYPE_STEEL_M2_FIRE":
    case ng.result.control.ControlTypeSteel.CONTROL_TYPE_STEEL_M2_FIRE: {
      return getName("control_type_steel_moment_weak_fire_");
    }
    case "CONTROL_TYPE_STEEL_V1":
    case ng.result.control.ControlTypeSteel.CONTROL_TYPE_STEEL_V1: {
      return getName("control_type_steel_shear_weak_");
    }
    case "CONTROL_TYPE_STEEL_V2":
    case ng.result.control.ControlTypeSteel.CONTROL_TYPE_STEEL_V2: {
      return getName("control_type_steel_shear_strong_");
    }
    case "CONTROL_TYPE_STEEL_T":
    case ng.result.control.ControlTypeSteel.CONTROL_TYPE_STEEL_T: {
      return getName("control_type_steel_torsional_");
    }
    case "CONTROL_TYPE_STEEL_NORMAL":
    case ng.result.control.ControlTypeSteel.CONTROL_TYPE_STEEL_NORMAL: {
      return getName("control_type_steel_normal_force_");
    }
    case "CONTROL_TYPE_STEEL_PURE_NORMAL":
    case ng.result.control.ControlTypeSteel.CONTROL_TYPE_STEEL_PURE_NORMAL: {
      return getName("control_type_steel_pure_normal_");
    }
    case "CONTROL_TYPE_STEEL_TAU":
    case ng.result.control.ControlTypeSteel.CONTROL_TYPE_STEEL_TAU: {
      return getName("control_type_steel_shear_stress_");
    }
    case "CONTROL_TYPE_STEEL_SIGMA":
    case ng.result.control.ControlTypeSteel.CONTROL_TYPE_STEEL_SIGMA: {
      return getName("control_type_steel_sigma_");
    }
    case "CONTROL_TYPE_STEEL_FB1":
    case ng.result.control.ControlTypeSteel.CONTROL_TYPE_STEEL_FB1: {
      return getName("control_type_steel_fb_strong_");
    }
    case "CONTROL_TYPE_STEEL_FB2":
    case ng.result.control.ControlTypeSteel.CONTROL_TYPE_STEEL_FB2: {
      return getName("control_type_steel_fb_weak_");
    }
    case "CONTROL_TYPE_STEEL_FTB":
    case ng.result.control.ControlTypeSteel.CONTROL_TYPE_STEEL_FTB: {
      return getName("control_type_steel_tfb_");
    }
    case "CONTROL_TYPE_STEEL_LTB_TOP":
    case ng.result.control.ControlTypeSteel.CONTROL_TYPE_STEEL_LTB_TOP: {
      return getName("control_type_steel_ltb_top_");
    }
    case "CONTROL_TYPE_STEEL_LTB_BOTTOM":
    case ng.result.control.ControlTypeSteel.CONTROL_TYPE_STEEL_LTB_BOTTOM: {
      return getName("control_type_steel_ltb_bottom_");
    }
    case "CONTROL_TYPE_STEEL_WEB":
    case ng.result.control.ControlTypeSteel.CONTROL_TYPE_STEEL_WEB: {
      return getName("control_type_steel_web_");
    }
    case "CONTROL_TYPE_STEEL_IA1":
    case ng.result.control.ControlTypeSteel.CONTROL_TYPE_STEEL_IA1: {
      return getName("control_type_steel_interaction_1_");
    }
    case "CONTROL_TYPE_STEEL_IA2":
    case ng.result.control.ControlTypeSteel.CONTROL_TYPE_STEEL_IA2: {
      return getName("control_type_steel_interaction_2_");
    }
    case "CONTROL_TYPE_STEEL_IA2ND":
    case ng.result.control.ControlTypeSteel.CONTROL_TYPE_STEEL_IA2ND: {
      return getName("control_type_steel_interaction_2nd_");
    }
  }
}

/** Returns a localized string for given timber control type (used in code check etc.).
 * @param db localisation database
 * @param lang requested language
 * @param type current control type (string or enum)
 * @param shortName retruns the short version of the name
 */
export function getTimberControlTypeName(
  db: any,
  lang: Lang,
  type: string | ng.result.control.ControlTypeTimber,
): string;
export function getTimberControlTypeName(
  db: any,
  lang: Lang,
  type: string | ng.result.control.ControlTypeTimber,
  shortName: boolean,
): string;
export function getTimberControlTypeName(
  db: any,
  lang: Lang,
  type: string | ng.result.control.ControlTypeTimber,
  shortName: boolean,
  showDesc: boolean,
  placement: Placement,
): string | JSX.Element;
export function getTimberControlTypeName(
  db: any,
  lang: Lang,
  type: string | ng.result.control.ControlTypeTimber,
  shortName: boolean = false,
  showDesc = false,
  placement = "right" as Placement,
): string | JSX.Element {
  const getName = (tag: string) => {
    return shortName
      ? getLabel(db, lang, tag + "short_", showDesc, false, placement)
      : getLabel(db, lang, tag, showDesc, false, placement);
  };

  switch (type) {
    default:
    case "CONTROL_TYPE_TIMBER_UNSPECIFIED":
    case ng.result.control.ControlTypeTimber.CONTROL_TYPE_TIMBER_UNSPECIFIED: {
      return getName("control_type_timber_unspecified_");
    }
    case "CONTROL_TYPE_TIMBER_OVERALL":
    case ng.result.control.ControlTypeTimber.CONTROL_TYPE_TIMBER_OVERALL: {
      return getName("control_type_timber_overall_");
    }
    case "CONTROL_TYPE_TIMBER_TENSION":
    case ng.result.control.ControlTypeTimber.CONTROL_TYPE_TIMBER_TENSION: {
      return getName("control_type_timber_combined_bending_tension_");
    }
    case "CONTROL_TYPE_TIMBER_COMPRESSION":
    case ng.result.control.ControlTypeTimber.CONTROL_TYPE_TIMBER_COMPRESSION: {
      return getName("control_type_timber_combined_bending_compression_");
    }
    case "CONTROL_TYPE_TIMBER_SHEAR":
    case ng.result.control.ControlTypeTimber.CONTROL_TYPE_TIMBER_SHEAR: {
      return getName("control_type_timber_combined_shear_torsion_");
    }
    case "CONTROL_TYPE_TIMBER_FLEXURAL_BUCKLING1":
    case ng.result.control.ControlTypeTimber.CONTROL_TYPE_TIMBER_FLEXURAL_BUCKLING1: {
      return getName("control_type_timber_flexural_buckling1_");
    }
    case "CONTROL_TYPE_TIMBER_FLEXURAL_BUCKLING2":
    case ng.result.control.ControlTypeTimber.CONTROL_TYPE_TIMBER_FLEXURAL_BUCKLING2: {
      return getName("control_type_timber_flexural_buckling2_");
    }
    case "CONTROL_TYPE_TIMBER_TORSIONAL_BUCKLING":
    case ng.result.control.ControlTypeTimber.CONTROL_TYPE_TIMBER_TORSIONAL_BUCKLING: {
      return getName("control_type_timber_lateral_torsional_buckling_");
    }
    case "CONTROL_TYPE_TIMBER_APEX":
    case ng.result.control.ControlTypeTimber.CONTROL_TYPE_TIMBER_APEX: {
      return getName("control_type_timber_bending_apex_");
    }
  }
}

/** Returns a display string for a value.
 * @param val (either a number, string or a object)
 * @param db localisation
 * @param tag a string that for example could be "displacement_", "moment_", "normalforce_", "shearforce_",
 * @param settings viewport.context.user.settings, if null or undefined it is considered not in use and default settings will be applied
 */
export type ValType = string | number | { [id: string]: any };
export const getValues = (props: {
  val: ValType;
  db: ITranslationDatabase;
  tag: string;
  settings: Settings | null | undefined;
}): string => {
  if (typeof props.val === "string") {
    return props.val;
  }

  const scaleFactor = props.settings
    ? getScaleFactor({
      db: props.db,
      tag: props.tag,
      settings: props.settings,
    })
    : 1;

  if (typeof props.val !== "object") {
    // val is a number
    return GetValLabel(props.val * scaleFactor);
  } else {
    let str = "";
    let obj = { ...props.val };
    for (const key in obj) {
      const num = Number(obj[key]);
      const val = GetValLabel(num * scaleFactor);

      str += `${key}: ${val} `;
    }
    return str;
  }
};

/** Returns a display string for a value, given a set number of decimals.
 * @param val (either a number, string or a object)
 * @param db localisation
 * @param tag a string that for example could be "displacement_", "moment_", "normalforce_", "shearforce_",
 * @param settings viewport.context.user.settings
 */
export const getValuesDec = (props: {
  val: string | number | { [id: string]: any };
  db: ITranslationDatabase;
  tag: string;
  settings: Settings;
  decimals: number;
}): string => {
  if (typeof props.val === "string") {
    return props.val;
  }

  const scaleFactor = getScaleFactor({
    db: props.db,
    tag: props.tag,
    settings: props.settings,
  });

  if (typeof props.val !== "object") {
    // val is a number
    return GetValLabelDec(props.val * scaleFactor, props.decimals);
  } else {
    let str = "";
    let obj = { ...props.val };
    for (const key in obj) {
      const num = Number(obj[key]);
      const val = GetValLabelDec(num * scaleFactor, props.decimals);

      str += `${key}: ${val} `;
    }
    return str;
  }
};

export function getBucklingPositions(indata: ng.stage.IData, loc: string) {
  let pos: number[] = [];

  for (let sup of indata.sups || []) {
    if (sup.lateral) {
      if (loc === "edge") {
        if (sup.supportLevel === ng.stage.SupportLevel.SUPPORT_LEVEL_TOP) {
          //&& sup.con?.transY?.value !== 0
          pos.push(sup.getCoord()?.x || 0);
        }
      }

      if (loc === "btm") {
        if (sup.supportLevel === ng.stage.SupportLevel.SUPPORT_LEVEL_BOTTOM) {
          //&& sup.con?.transY?.value !== 0
          pos.push(sup.getCoord()?.x || 0);
        }
      }

      if (loc === "top") {
        if (sup.supportLevel === ng.stage.SupportLevel.SUPPORT_LEVEL_TOP) {
          //&& sup.con?.transY?.value !== 0
          pos.push(sup.getCoord()?.x || 0);
        }
      }

      if (loc === "weak") {
        if (sup.supportLevel === ng.stage.SupportLevel.SUPPORT_LEVEL_CENTER) {
          //&& sup.con?.transY?.value !== 0
          pos.push(sup.getCoord()?.x || 0);
        }
      }
    } else {
      if (loc === "stiff") {
        pos.push(sup.getCoord()?.x || 0);
      }
    }
  }

  pos = [...new Set(pos)]; //remove duplicates

  pos.sort((a, b) => a - b); //sort ascending

  return pos;
}

export const updateBuckPosAndSpan = (
  supGuid: string,
  val: number,
  length: number,
  beam: ng.beam.IData,
  stage: ng.stage.IData,
  action: string,
): [ng.beam.Data, ng.stage.Data] | undefined => {
  let tmpSups = stage.sups.map((s) => s.deepClone());
  let sup = tmpSups.find((sup: ng.stage.ISupport) => sup.guid === supGuid);

  let prevVal: number = 0;

  if (action !== "chgLen") {
    const supCoord = sup?.getCoord();
    if (supCoord) {
      prevVal = supCoord.x;
    }
  } else {
    prevVal = val;
    val = length;
  }
  val = Math.round(val * 1000) / 1000;
  prevVal = Math.round(prevVal * 1000) / 1000;

  if (val < 0) {
    val = 0;
  }
  if (val > length) {
    val = length;
  }

  let tol = 1e-3;

  // Early return if sup is moved to on of the ends of the beam ------------------------
  if (
    (Math.abs(prevVal) < tol && Math.abs(val - length) < tol) ||
    (Math.abs(prevVal - length) < tol && Math.abs(val) < tol)
  ) {
    const supCoord = sup?.getCoord();
    if (supCoord) {
      supCoord.x = val;

      stage.sups = tmpSups;
    }

    return [beam.deepClone(), stage.deepClone()];
  }

  let tmpPos: number[] = [];
  let tmpBetas: number[] = [];
  let tmpLtsCond: ng.beam.SupportCondition[] = [];

  //========================== H E L P   F U N C T I O N  ========================================

  function arrangeBuckling(
    loc: string,
    buckData: any,
    level: ng.stage.SupportLevel,
    action: string,
    prevVal: number,
    val: number,
  ) {
    // positions -------------------------------------

    tmpPos = [];

    let nbrOfRemovedPos = 0;

    if (loc !== "stiff") {
      tmpPos = getBucklingPositions(stage, loc) || [];

      //if lateral supports are outside of beam -> remove them
      let tmp = structuredClone(tmpPos);
      for (let pos of tmp) {
        if (pos > length) {
          nbrOfRemovedPos++;
          tmpPos.splice(tmpPos.indexOf(pos), 1);
        }
      }
    }

    // Gather the positions prior to change -------------------------------------

    //if normal (not lateral) supports are at this level -> include them as well
    let change = false;
    for (let sup of tmpSups || []) {
      if ((!sup.lateral && sup.supportLevel === level) || (!sup.lateral && loc === "stiff")) {
        change = true;
        const supCoord = sup.getCoord();
        if (supCoord?.x) {
          if (supCoord.x > length) {
            supCoord.x = length;
            stage.sups = tmpSups; // update support position
          }
          tmpPos.push(supCoord.x);
        }
      }
    }

    //add start and end of beam
    tmpPos.unshift(0);
    tmpPos.push(length);

    tmpPos = [...new Set(tmpPos)]; //remove duplicates
    tmpPos.sort(function (a, b) {
      return a - b;
    }); // order

    //NOTE! 'tmpPos' is now an array of how the spans were befored the change

    // ------------------------------------------------------------------

    // decide which span(s) that is/are affected by the change

    let startSpan = 0;
    for (let i = 0; i < tmpPos.length; i++) {
      if (prevVal > tmpPos[i]) {
        startSpan++;
      } else {
        break;
      }
    }

    let endSpan = 0;
    for (let i = 0; i < tmpPos.length; i++) {
      if (val > tmpPos[i]) {
        endSpan++;
      } else {
        break;
      }
    }

    // betas -------------------------------------

    if (loc === "edge") {
      tmpLtsCond = buckData.supportConditions;
    } else {
      tmpBetas = buckData.betas;
    }

    if (nbrOfRemovedPos > 0) {
      if (loc === "edge") {
        for (let i = 0; i < nbrOfRemovedPos; i++) {
          tmpLtsCond.pop();
        }
      } else {
        for (let i = 0; i < nbrOfRemovedPos; i++) {
          tmpBetas.pop();
        }
      }
    }

    // organize -------------------------------------

    if (change) {
      if (action === "chgSup") {
        if (prevVal === 0) {
          action = "add";
        } else {
          action = "move";
        }

        console.log("action", action);

        if (action === "add") {
          //this will split the affected span into two spans and set both betas to 1.0
          tmpPos.splice(endSpan, 0, val);
          if (loc === "edge") {
            tmpLtsCond.splice(endSpan, 0, ng.beam.SupportCondition.SUPPORT_CONDITION_SIMPLY);
            tmpLtsCond[endSpan - 1] = ng.beam.SupportCondition.SUPPORT_CONDITION_SIMPLY;
          } else {
            tmpBetas.splice(endSpan, 0, 1.0);
            tmpBetas[endSpan - 1] = 1.0;
          }
        }

        if (action === "move") {
          // 'move' is equal to 'remove' and then 'add'

          // first remove the old position
          if (loc === "edge") {
            tmpLtsCond[startSpan - 1] = ng.beam.SupportCondition.SUPPORT_CONDITION_SIMPLY;
            tmpLtsCond.splice(startSpan, 1);
          } else {
            tmpBetas[startSpan - 1] = 1.0;
            tmpBetas.splice(startSpan, 1);
          }

          if (startSpan < endSpan) {
            endSpan--; //if we remove a position, the endSpan will be one less
          }

          // then add the new position
          tmpPos.splice(endSpan, 0, val);

          //secure that we always have a position at the ends of the beam-------------------------------------
          //add start and end of beam
          tmpPos.unshift(0);
          tmpPos.push(length);

          tmpPos = [...new Set(tmpPos)]; //remove duplicates
          tmpPos.sort(function (a, b) {
            return a - b;
          }); // order
          //---------------------------------------------------------------------

          if (loc === "edge") {
            tmpLtsCond.splice(endSpan, 0, ng.beam.SupportCondition.SUPPORT_CONDITION_SIMPLY);
            tmpLtsCond[endSpan - 1] = ng.beam.SupportCondition.SUPPORT_CONDITION_SIMPLY;
          } else {
            tmpBetas.splice(endSpan, 0, 1.0);
            tmpBetas[endSpan - 1] = 1.0;
          }
        }
      }

      if (action === "delSup") {
        console.log("delSup", startSpan, endSpan, tmpPos.length);

        if (startSpan !== tmpPos.length - 1 && startSpan !== 0) {
          // never remove end of beam
          tmpPos.splice(startSpan, 1);

          if (loc === "edge") {
            tmpLtsCond.splice(startSpan, 1);
          } else {
            tmpBetas.splice(startSpan, 1);
          }
        }
      }

      if (action === "chgLen") {
        if (val > prevVal) {
          //elongate
          if (stage.sups?.find((sup: ng.stage.ISupport) => sup.getCoord()?.x === prevVal)) {
            // is there a support at the old end of the beam?
            if (loc === "edge") {
              tmpLtsCond.push(ng.beam.SupportCondition.SUPPORT_CONDITION_SIMPLY);
            } else {
              tmpBetas.push(1.0);
            }
          }
        } else {
          //shorten
          let delta = 0;
          if (loc === "edge") {
            delta = tmpLtsCond.length - (tmpPos.length - 1);
            tmpLtsCond.splice(tmpLtsCond.length - 1 - delta, delta);
          } else {
            delta = tmpBetas.length - (tmpPos.length - 1);
            tmpBetas.splice(tmpBetas.length - 1 - delta, delta);
          }
        }
      }
    }

    if (loc === "edge") {
      buckData.supportCondition = structuredClone(tmpLtsCond);
    } else {
      buckData.betas = structuredClone(tmpBetas);
    }

    // spans -------------------------------------

    buckData.data = [];

    if (loc === "edge") {
      for (let i = 1; i < tmpPos.length; i++) {
        let span = ng.beam.BucklingData.create({
          position: ng.beam.BucklingSpan.create({
            start: tmpPos[i - 1],
            end: tmpPos[i],
          }),
          supportCondition: tmpLtsCond[i - 1],
        });

        buckData.data.push(span);
      }
    } else {
      for (let i = 1; i < tmpPos.length; i++) {
        let span = ng.beam.BucklingData.create({
          position: ng.beam.BucklingSpan.create({
            start: tmpPos[i - 1],
            end: tmpPos[i],
          }),
          beta: tmpBetas[i - 1],
        });

        buckData.data.push(span);
      }
    }

    console.log("buckData", buckData);
  }

  function updateSupports(action: string) {
    if (stage.sups) {
      let ind = stage.sups.findIndex((o) => {
        return (o.lateral && (o.getCoord()?.x || 0) > length) || false;
      });
      if (ind >= 0) {
        stage.sups?.splice(ind, 1);
      }
    }

    if (action === "chgSup") {
      if (prevVal === 0) {
        action = "add";
      } else {
        action = "move";
      }

      if (action === "add") {
        const sup = tmpSups.find((sup: ng.stage.ISupport) => sup.guid === supGuid);
        const supCoord = sup?.getCoord();
        if (supCoord) {
          supCoord.x = val;

          stage.sups = tmpSups;
        }
      }

      if (action === "move") {
        const sup = tmpSups.find((sup: ng.stage.ISupport) => sup.guid === supGuid);
        const supCoord = sup?.getCoord();
        if (supCoord) {
          supCoord.x = val;

          stage.sups = tmpSups;
        }
      }
    }
    if (action === "delSup") {
      let ind = tmpSups.findIndex((sup: ng.stage.ISupport) => sup.guid === supGuid);

      if (ind != null && ind > -1) {
        tmpSups.splice(ind, 1);
      }

      stage.sups = tmpSups;
    }
  }

  //================================================================================================

  if (beam) {
    if (beam.element?.type === "steel") {
      // top flange ------------------------------
      if (beam.element.steel.ltbTop) {
        arrangeBuckling(
          "top",
          beam.element.steel.ltbTop,
          ng.stage.SupportLevel.SUPPORT_LEVEL_TOP,
          action,
          prevVal,
          val,
        );
      }

      //bottom flange --------------------------------
      if (beam.element.steel.ltbBtm) {
        arrangeBuckling(
          "btm",
          beam.element.steel.ltbBtm,
          ng.stage.SupportLevel.SUPPORT_LEVEL_BOTTOM,
          action,
          prevVal,
          val,
        );
      }

      //stiff direction --------------------------------
      if (beam.element.steel.fbStiff) {
        arrangeBuckling(
          "stiff",
          beam.element.steel.fbStiff,
          ng.stage.SupportLevel.SUPPORT_LEVEL_UNSPECIFIED,
          action,
          prevVal,
          val,
        );
      }

      //weak direction --------------------------------
      if (beam.element.steel.fbWeak) {
        arrangeBuckling(
          "weak",
          beam.element.steel.fbWeak,
          ng.stage.SupportLevel.SUPPORT_LEVEL_CENTER,
          action,
          prevVal,
          val,
        );
      }
    } else if (beam.element?.type === "timber") {
      //edge --------------------------------
      if (beam.element.timber.ltsb) {
        arrangeBuckling(
          "edge",
          beam.element.timber.ltsb,
          ng.stage.SupportLevel.SUPPORT_LEVEL_TOP,
          action,
          prevVal,
          val,
        );
      }

      //stiff direction --------------------------------
      if (beam.element.timber.fbStiff) {
        arrangeBuckling(
          "stiff",
          beam.element.timber.fbStiff,
          ng.stage.SupportLevel.SUPPORT_LEVEL_UNSPECIFIED,
          action,
          prevVal,
          val,
        );
      }

      //weak direction --------------------------------
      if (beam.element.timber.fbWeak) {
        arrangeBuckling(
          "weak",
          beam.element.timber.fbWeak,
          ng.stage.SupportLevel.SUPPORT_LEVEL_CENTER,
          action,
          prevVal,
          val,
        );
      }
    }

    // update normal supports
    updateSupports(action);

    return [beam.deepClone(), stage.deepClone()];
  }
};

export const updateStiffsPos = (stiffs: ng.beam.IStiffener[], elemL: number) => {
  function updatePos(tmpStiffs: ng.beam.IStiffener[], ind: string, val: number) {
    if (val < 0) val = 0;

    if (val > elemL) val = elemL;

    tmpStiffs[parseInt(ind)].pos = val;

    return tmpStiffs;
  }

  function removeDuplicateStiffs(tmpStiffs: ng.beam.IStiffener[]) {
    for (let i = 0; i < tmpStiffs.length; i++) {
      tmpStiffs.findIndex((m, ind) => {
        if (m.pos === tmpStiffs[i].pos && ind !== i) {
          tmpStiffs?.splice(ind, 1);
          i = 0;
          return true;
        }
        return false;
      });
    }

    return tmpStiffs;
  }

  //---------------------------------------------

  for (let index = 0; index < stiffs.length; index++) {
    let stiff = stiffs[index];
    if (stiff && stiff.pos) {
      stiffs = updatePos(stiffs, index.toString(), stiff.pos);
    }
  }

  stiffs = removeDuplicateStiffs(stiffs);

  return stiffs;
};

//---------------------------------------------
export const createOutputPositions = (num: number, length: number, height: number, conc: boolean): number[] => {
  // Create an empty lisst of positions
  let tmpPos: number[] = [];

  // Don't allow smaller distance between positions than 0.1m
  if (length / num < 0.1) {
    num = Math.floor(length / 0.1);
  }

  // But make sure we have at least 21 positions i.e. 20 divisions
  if (num < 21) {
    num = 21;
  }

  if (conc) {
    // Don't allow larger distance between positions than H/2
    if (length / num > height / 2) {
      num = Math.ceil(length / (height / 2)) + 1;
    }
  }

  // Create the positions
  for (let i = 0; i < num; i++) {
    let x = (i * length) / (num - 1);
    x = parseFloat(x.toFixed(3)); // Round to 3 decimals
    tmpPos.push(x);
  }

  return tmpPos;
};

// Finds and returns current beam section. Assuming that the first element in the first structure is the one we are interested in
export const getCurrentBeamSection = (input: ng.input.Data): ng.sections.Section => {
  if (input && input.structures && input.structures.length > 0) {
    let s = input.structures[0];
    if (s && s.elements && s.elements.length > 0) {
      const beam = s.elements[0].getBeam();
      if (beam && beam.segments.length > 0) {
        let seg = beam.segments[0];
        if (seg && seg.layer && seg.layer.start && seg.layer.start.secGuid) {
          let secGuid = seg.layer.start.secGuid;
          for (let sec of input.secDb) {
            if (sec && sec.id && sec.id.guid === secGuid) {
              return sec;
            }
          }
        }
      }
    }
  }

  return ng.sections.Section.create({ id: ng.utils.ID.create({ name: "UNDEFINED", guid: "" }) });
};

// Finds and returns current beam reinforcement. Assuming that the first element in the first structure is the one we are interested in.
// From the groups and layers a representation of the reinforcement is created.
// e.g. "2 Ø 12 + 3 Ø 16" for bottom reinforcement
export const getCurrentBeamReinforcementString = (input: ng.input.Data) => {
  let btm = "";
  let top = "";
  let stirrups = "";

  if (input && input.structures && input.structures.length > 0) {
    let s = input.structures[0];
    if (s && s.elements && s.elements.length > 0) {
      const e = s.elements[0];
      let beam = e.getBeam();
      if (beam && beam.element?.type === "concrete" && beam.element.concrete.rebars) {
        let concColumnDesSet = e?.designSettings?.rc?.getColumn();
        let groups = beam.element.concrete.rebars.grps;
        let layers = beam.element.concrete.rebars.lays;
        let links: ng.link.Data = ng.link.Data.create();
        if (
          concColumnDesSet?.shearDesignType ===
          ng.design.concrete.ShearDesignType.SHEAR_DESIGN_TYPE_WITH_SHEAR_REINFORCEMENT
        ) {
          if (beam.element.concrete.links !== null) {
            links = beam.element.concrete.links;
          }
        }

        if (layers && groups) {
          let topLayers = layers.filter((l) => l.zone === ng.design.concrete.BeamSide.BEAM_SIDE_TOP);
          let topReinf: { [diam: number]: number } = {};
          for (let l of topLayers) {
            let group = groups.find((g) => g.id?.guid === l.grpGuid);
            if (group && l.numbers?.type === "num") {
              if (group.diameter in topReinf) {
                topReinf[group.diameter] += l.numbers.num;
              } else {
                topReinf[group.diameter] = l.numbers.num;
              }
            }
          }
          let first = true;
          for (let diam in topReinf) {
            if (first) {
              first = false;
              top += topReinf[diam] + " Ø " + diam;
            } else {
              top += " + " + topReinf[diam] + " Ø " + diam;
            }
          }

          let btmLayers = layers.filter((l) => l.zone === ng.design.concrete.BeamSide.BEAM_SIDE_BOTTOM);
          let btmReinf: { [diam: number]: number } = {};
          for (let l of btmLayers) {
            let group = groups.find((g) => g.id?.guid === l.grpGuid);
            if (group && l.numbers?.type === "num") {
              if (group.diameter in btmReinf) {
                btmReinf[group.diameter] += l.numbers.num;
              } else {
                btmReinf[group.diameter] = l.numbers.num;
              }
            }
          }
          first = true;
          for (let diam in btmReinf) {
            if (first) {
              first = false;
              btm += btmReinf[diam] + " Ø " + diam;
            } else {
              btm += " + " + btmReinf[diam] + " Ø " + diam;
            }
          }
        }

        if (links && links.linkGrps && links.linkGrps.length > 0) {
          let lg0 = links.linkGrps[0];
          stirrups = "Ø " + lg0.diameter * 1.0e3 + " s" + lg0.s * 1.0e3;
          for (let i = 1; i < links.linkGrps.length; i++) {
            let lg = links.linkGrps[i];
            stirrups += " + Ø " + lg.diameter * 1.0e3 + " s" + lg.s * 1.0e3;
          }
        }
      }
    }
  }

  return { btm: btm, top: top, stirrups: stirrups };
};

// Get the current beam concrete name
export const getCurrentBeamConcreteName = (input: ng.input.Data) => {
  let concName = "";

  if (input && input.structures && input.structures.length > 0) {
    let s = input.structures[0];
    if (s && s.elements && s.elements.length > 0) {
      let e = s.elements[0];
      const beam = e.getBeam();
      if (beam?.element?.type === "concrete") {
        let guid = beam.mtrlGuid;
        let conc = input.mtrlDb.find((m) => m.id?.guid === guid);
        concName = conc?.id?.name || "";
      }
    }
  }

  return concName;
};

// Initializes the design changes for the concrete design and sets the "before" values
export const prepareConcreteDesignChanges = (
  input: ng.input.Data,
  changes: { [type: string]: IReportedDesignChange },
) => {
  // Guards
  if (
    !input ||
    !input.structures ||
    input.structures.length === 0 ||
    !input.structures[0].elements ||
    input.structures[0].elements.length === 0
  ) {
    return;
  }

  // Get element
  const e = input.structures[0].elements[0];

  // Get current design settings
  const concColumnDesSet = e.designSettings?.rc?.getColumn();
  if (!concColumnDesSet) {
    return;
  }

  // Get current reinforcement and concrete
  let r = getCurrentBeamReinforcementString(input);
  let name = getCurrentBeamConcreteName(input);

  // Prepare changes, i.e. set the "previous" values and reset the suggested values
  // which will be set in the "update" version of this function.
  changes["reinforcement_top_"] = {
    type: "reinforcement_top_",
    previous: r.top,
    suggested: "",
  } as IReportedDesignChange;
  changes["reinforcement_bottom_"] = {
    type: "reinforcement_bottom_",
    previous: r.btm,
    suggested: "",
  } as IReportedDesignChange;
  if (
    concColumnDesSet.shearDesignType === ng.design.concrete.ShearDesignType.SHEAR_DESIGN_TYPE_WITH_SHEAR_REINFORCEMENT
  ) {
    changes["stirrups_"] = { type: "stirrups_", previous: r.stirrups, suggested: "" } as IReportedDesignChange;
  }
  changes["concrete_"] = { type: "concrete_", previous: name, suggested: "" } as IReportedDesignChange;
};

// Updated the design changes for the concrete design and sets the "after" values
export const updateConcreteDesignChanges = (
  input: ng.input.Data,
  changes: { [type: string]: IReportedDesignChange },
) => {
  // Guards
  if (
    !input ||
    !input.structures ||
    input.structures.length === 0 ||
    !input.structures[0].elements ||
    input.structures[0].elements.length === 0
  ) {
    return;
  }

  // Get element
  const e = input.structures[0].elements[0];

  // Get current design settings
  const concColumnDesSet = e.designSettings?.rc?.getColumn();
  if (!concColumnDesSet) {
    return;
  }

  // Get current reinforcement and concrete
  let r = getCurrentBeamReinforcementString(input);
  let name = getCurrentBeamConcreteName(input);

  // Update changes, i.e. set the the suggested values that will be compared with the previous values
  changes["reinforcement_top_"].suggested = r.top;
  changes["reinforcement_bottom_"].suggested = r.btm;
  if (
    concColumnDesSet.shearDesignType === ng.design.concrete.ShearDesignType.SHEAR_DESIGN_TYPE_WITH_SHEAR_REINFORCEMENT
  ) {
    changes["stirrups_"].suggested = r.stirrups;
  }
  changes["concrete_"].suggested = name;
};

// Initializes the design changes for the steel design and sets the "before" values
export const prepareSteelDesignChanges = (input: ng.input.Data, changes: { [type: string]: IReportedDesignChange }) => {
  // Guards
  if (
    !input ||
    !input.structures ||
    input.structures.length === 0 ||
    !input.structures[0].elements ||
    input.structures[0].elements.length === 0
  ) {
    return;
  }

  // Get element
  const e = input.structures[0].elements[0];

  // More guards
  if (!e || !e.designSettings || !e.designSettings.steel || !e.designSettings.steel.fire) {
    return;
  }

  // Get current beam section
  let s = getCurrentBeamSection(input);
  if (!s || !s.id || !s.id.name) {
    return;
  }

  // Get current design settings
  let stlDesFire = e.designSettings.steel.fire;

  // Prepare changes, i.e. set the "previous" values and reset the suggested values
  // which will be set in the "update" version of this function.
  changes["cross_section_"] = { type: "cross_section_", previous: s.id.name, suggested: "" } as IReportedDesignChange;
  if (stlDesFire !== null) {
    if (stlDesFire.protection_type?.type === "protectingMaterial") {
      let thickness = stlDesFire.protection_type.protectingMaterial.thickness * 1.0e3; // m -> mm
      changes["fire_protection_thickness_"] = {
        type: "fire_protection_thickness_",
        previous: thickness.toString() + " mm",
        suggested: "",
      } as IReportedDesignChange;
    } else if (stlDesFire.protection_type?.type === "temperature") {
      let temperature = stlDesFire.protection_type.temperature;
      changes["fire_protection_temperature_"] = {
        type: "fire_protection_temperature_",
        previous: temperature.toString(),
        suggested: "",
      } as IReportedDesignChange;
    }
  }
};

// Updated the design changes for the steel design and sets the "after" values
export const updateSteelDesignChanges = (input: ng.input.Data, changes: { [type: string]: IReportedDesignChange }) => {
  // Guards
  if (
    !input ||
    !input.structures ||
    input.structures.length === 0 ||
    !input.structures[0].elements ||
    input.structures[0].elements.length === 0
  ) {
    return;
  }

  // Get element
  const e = input.structures[0].elements[0];

  // More guards
  if (!e || !e.designSettings || !e.designSettings.steel || !e.designSettings.steel.fire) {
    return;
  }

  // Get current beam section
  let s = getCurrentBeamSection(input);
  if (!s || !s.id || !s.id.name) {
    return;
  }

  // Get current design settings
  let stlDesFire = e.designSettings.steel.fire;

  // Update changes, i.e. set the the suggested values that will be compared with the previous values
  changes["cross_section_"].suggested = s.id.name;
  if (stlDesFire !== null) {
    if (stlDesFire.protection_type?.type === "protectingMaterial") {
      let thickness = stlDesFire.protection_type.protectingMaterial.thickness * 1.0e3; // m -> mm
      changes["fire_protection_thickness_"].suggested = thickness.toString() + " mm";
    } else if (stlDesFire.protection_type?.type === "temperature") {
      let temperature = stlDesFire.protection_type.temperature;
      changes["fire_protection_temperature_"].suggested = temperature.toString();
    }
  }
};

// Initializes the design changes for the timber design and sets the "before" values
export const prepareTimberDesignChanges = (
  input: ng.input.Data,
  changes: { [type: string]: IReportedDesignChange },
) => {
  // Guards
  if (
    !input ||
    !input.structures ||
    input.structures.length === 0 ||
    !input.structures[0].elements ||
    input.structures[0].elements.length === 0
  ) {
    return;
  }

  // Get element
  const e = input.structures[0].elements[0];

  // More guards
  if (!e || !e.designSettings || !e.designSettings.timber || !e.designSettings.timber.fire) {
    return;
  }

  // Get current beam section
  let s = getCurrentBeamSection(input);
  if (!s || !s.id || !s.id.name) {
    return;
  }

  // Get current design settings
  let timDesFire = e.designSettings.timber.fire;

  // Prepare changes, i.e. set the "previous" values and reset the suggested values
  // which will be set in the "update" version of this function.
  changes["cross_section_"] = { type: "cross_section_", previous: s.id.name, suggested: "" } as IReportedDesignChange;
  if (
    timDesFire !== null &&
    timDesFire.protection !== null &&
    timDesFire.protection.mtrl !== null &&
    timDesFire.protection.mtrl.properties !== null
  ) {
    switch (timDesFire.protection.mtrl.properties.materialType) {
      case ng.fire_protection.timber.MaterialType.MATERIAL_TYPE_WOOD:
      case ng.fire_protection.timber.MaterialType.MATERIAL_TYPE_GYPSUM_BOARD_AH1_INTERNAL:
      case ng.fire_protection.timber.MaterialType.MATERIAL_TYPE_GYPSUM_BOARD_AH1_OTHER:
      case ng.fire_protection.timber.MaterialType.MATERIAL_TYPE_GYPSUM_BOARD_F1_INTERNAL:
      case ng.fire_protection.timber.MaterialType.MATERIAL_TYPE_GYPSUM_BOARD_F1_OTHER:
      case ng.fire_protection.timber.MaterialType.MATERIAL_TYPE_ROCK_FIBER:
        {
          let t = timDesFire.protection.mtrl.properties.thickness * 1.0e3; // m -> mm
          changes["fire_protection_thickness_"] = {
            type: "fire_protection_thickness_",
            previous: t.toFixed(1) + " mm",
            suggested: "",
          } as IReportedDesignChange;
        }
        break;
      case ng.fire_protection.timber.MaterialType.MATERIAL_TYPE_GYPSUM_BOARD_AH2_INTERNAL:
      case ng.fire_protection.timber.MaterialType.MATERIAL_TYPE_GYPSUM_BOARD_AH2_OTHER:
      case ng.fire_protection.timber.MaterialType.MATERIAL_TYPE_GYPSUM_BOARD_F2_INTERNAL:
      case ng.fire_protection.timber.MaterialType.MATERIAL_TYPE_GYPSUM_BOARD_F2_OTHER:
        {
          let to = timDesFire.protection.mtrl.properties.thicknessOuter * 1.0e3; // m -> mm
          let ti = timDesFire.protection.mtrl.properties.thicknessInner * 1.0e3; // m -> mm
          changes["fire_protection_thickness_outer_"] = {
            type: "fire_protection_thickness_outer_",
            previous: to.toFixed(1) + " mm",
            suggested: "",
          } as IReportedDesignChange;
          changes["fire_protection_thickness_inner_"] = {
            type: "fire_protection_thickness_inner_",
            previous: ti.toFixed(1) + " mm",
            suggested: "",
          } as IReportedDesignChange;
        }
        break;
      default:
        break;
    }
  }
};

// Updated the design changes for the timber design and sets the "after" values
export const updateTimberDesignChanges = (input: ng.input.Data, changes: { [type: string]: IReportedDesignChange }) => {
  // Guards
  if (
    !input ||
    !input.structures ||
    input.structures.length === 0 ||
    !input.structures[0].elements ||
    input.structures[0].elements.length === 0
  ) {
    return;
  }

  // Get element
  const e = input.structures[0].elements[0];

  // More guards
  if (!e || !e.designSettings || !e.designSettings.timber || !e.designSettings.timber.fire) {
    return;
  }

  // Get current beam section
  let s = getCurrentBeamSection(input);
  if (!s || !s.id || !s.id.name) {
    return;
  }

  // Get current design setting1
  let timDesFire = e.designSettings.timber.fire;

  // Update changes, i.e. set the the suggested values that will be compared with the previous values
  changes["cross_section_"].suggested = s.id.name;
  if (
    timDesFire !== null &&
    timDesFire.protection !== null &&
    timDesFire.protection.mtrl !== null &&
    timDesFire.protection.mtrl.properties !== null
  ) {
    switch (timDesFire.protection.mtrl.properties.materialType) {
      case ng.fire_protection.timber.MaterialType.MATERIAL_TYPE_WOOD:
      case ng.fire_protection.timber.MaterialType.MATERIAL_TYPE_GYPSUM_BOARD_AH1_INTERNAL:
      case ng.fire_protection.timber.MaterialType.MATERIAL_TYPE_GYPSUM_BOARD_AH1_OTHER:
      case ng.fire_protection.timber.MaterialType.MATERIAL_TYPE_GYPSUM_BOARD_F1_INTERNAL:
      case ng.fire_protection.timber.MaterialType.MATERIAL_TYPE_GYPSUM_BOARD_F1_OTHER:
      case ng.fire_protection.timber.MaterialType.MATERIAL_TYPE_ROCK_FIBER:
        {
          let t = timDesFire.protection.mtrl.properties.thickness * 1.0e3; // m -> mm
          changes["fire_protection_thickness_"].suggested = t.toFixed(1) + " mm";
        }
        break;
      case ng.fire_protection.timber.MaterialType.MATERIAL_TYPE_GYPSUM_BOARD_AH2_INTERNAL:
      case ng.fire_protection.timber.MaterialType.MATERIAL_TYPE_GYPSUM_BOARD_AH2_OTHER:
      case ng.fire_protection.timber.MaterialType.MATERIAL_TYPE_GYPSUM_BOARD_F2_INTERNAL:
      case ng.fire_protection.timber.MaterialType.MATERIAL_TYPE_GYPSUM_BOARD_F2_OTHER:
        {
          let to = timDesFire.protection.mtrl.properties.thicknessOuter * 1.0e3; // m -> mm
          let ti = timDesFire.protection.mtrl.properties.thicknessInner * 1.0e3; // m -> mm
          changes["fire_protection_thickness_outer_"].suggested = to.toFixed(1) + " mm";
          changes["fire_protection_thickness_inner_"].suggested = ti.toFixed(1) + " mm";
        }
        break;
      default:
        break;
    }
  }
};

export const CreatePolylineFromPolycurve = (sec: ng.sections.Section): ng.sections.Section => {
  //this function is needed since the perimeter part of the polyline is altered in analysis section and saved to the db

  // first: clean the polyline
  sec.polylines = ng.geometry.LineFace2D.create();

  let polycurves = sec.polycurves;
  if (polycurves) {
    let tmpFace = ng.geometry.LineFace2D.create({});

    let tmpPnts: ng.geometry.Point2D[] = [];
    let start: ng.geometry.Point2D = {} as ng.geometry.Point2D;
    polycurves.perimeter?.curves.forEach((c, i) => {
      if (c.segment?.type === "lineSegment" && c.segment.lineSegment.start) {
        tmpPnts.push(c.segment.lineSegment.start);
        if (i === 0) {
          start = c.segment.lineSegment.start.deepClone();
        }
      }
    });
    tmpPnts.push(start);

    tmpFace.perimeter = ng.geometry.PolyLine2D.create({ points: tmpPnts });

    polycurves.additionalPerimeters.forEach((add) => {
      tmpPnts = [];
      start = {} as ng.geometry.Point2D;
      add.curves.forEach((c, i) => {
        if (c.segment?.type === "lineSegment" && c.segment.lineSegment.start) {
          tmpPnts.push(c.segment.lineSegment.start);
          if (i === 0) {
            start = c.segment.lineSegment.start.deepClone();
          }
        }
      });
      tmpPnts.push(start);

      tmpFace.additionalPerimeters.push(ng.geometry.PolyLine2D.create({ points: tmpPnts }));
    });

    sec.polylines = tmpFace;
  }

  return sec;
};

export const CreatePolylineFromParams = (sec: ng.sections.Section): ng.sections.Section => {
  //this function is needed since the perimeter part of the polyline is altered in analysis section and saved to the db

  // first: clean the polyline
  sec.polylines = ng.geometry.LineFace2D.create({
    perimeter: ng.geometry.PolyLine2D.create({}),
    holes: [],
    additionalPerimeters: [],
  });

  let perim = [];
  let add = [];
  let adds = [];

  switch (sec.type) {
    case ng.sections.SectionType.SECTION_TYPE_HSQ:
      if (sec.params?.type === "hsqParams") {
        let h = sec.params.hsqParams.height;
        let w_top = sec.params.hsqParams.topWidth;
        let w = sec.params.hsqParams.width;
        let t_top_fl = sec.params.hsqParams.topFlangeThickness;
        let t_btm_fl = sec.params.hsqParams.bottomFlangeThickness;
        let t_web = sec.params.hsqParams.webThickness;
        let indent = sec.params.hsqParams.indent;
        let indent_cut = sec.params.hsqParams.indentCut;
        let cut = sec.params.hsqParams.cutOff;

        let b_lft = indent;
        let b_rgt = indent;

        if (cut === 1 || cut === 3) {
          b_lft = indent_cut;
        }

        if (cut === 2 || cut === 3) {
          b_rgt = indent_cut;
        }

        //================================================================================================

        // NOTE!!!
        // Identical code exist in NG-analysis-section repo in section.cpp file

        // derive x1's
        let tol = 1e-6;
        let h1 = h - t_btm_fl - t_top_fl / 2.0;

        const lamda = (dx: number, indent: number) => {
          let alfa = Math.atan(h1 / dx);

          let beta_low = alfa;
          let beta_high = Math.PI / 2.0;

          let beta = (beta_low + beta_high) / 2.0;

          let sol = h1 * Math.cos(beta) + t_web - dx * Math.sin(beta);

          while (Math.abs(sol) > tol) {
            if (sol > 0) {
              beta_low = beta;
            } else {
              beta_high = beta;
            }

            beta = (beta_low + beta_high) / 2.0;

            sol = h1 * Math.cos(beta) + t_web - dx * Math.sin(beta);
          }

          let x1 = t_web / Math.sin(beta);

          return x1;
        };

        let x0_lft = 0;
        let x1_lft = t_web;

        let x0_rgt = 0;
        let x1_rgt = t_web;

        // x1 left --------------------------------

        if (cut === 0) {
          let dx = (w - w_top) / 2.0 - b_lft;

          x1_lft = lamda(dx, b_lft);
          x0_lft = dx - x1_lft;
        }

        if (cut === 2) // how does left side behave when right side is cut off?
        {
          let dx = w - (b_rgt + t_web + w_top) - b_lft;

          x1_lft = lamda(dx, b_lft);
          x0_lft = dx - x1_lft;
        }

        // x1 right --------------------------------

        if (cut === 0) {
          let dx = (w - w_top) / 2.0 - b_rgt;

          x1_rgt = lamda(dx, b_rgt);
          x0_rgt = dx - x1_rgt;
        }

        if (cut === 1) // how does right side behave when left side is cut off?
        {
          let dx = w - (b_lft + t_web + w_top) - b_rgt;

          x1_rgt = lamda(dx, b_rgt);
          x0_rgt = dx - x1_rgt;
        }

        //-------------------------------------------

        if (cut === 3) {
          b_lft = b_rgt = (w - w_top) / 2 - t_web;
        }

        // --------------- POLYLINE -----------------------------

        // lower flange
        let x = 0.0,
          y = 0.0;
        let xs = x,
          ys = y;

        perim.push([xs, ys]);
        x += w;
        perim.push([x, y]);
        y += t_btm_fl;
        perim.push([x, y]);
        x -= w;
        perim.push([x, y]);
        perim.push([xs, ys]);

        // top flange
        add = [];
        x = b_lft + x0_lft + x1_lft;
        y = h - t_top_fl;

        xs = x;
        ys = y;

        add.push([xs, ys]);
        x += w_top;
        add.push([x, y]);
        y += t_top_fl;
        add.push([x, y]);
        x -= w_top;
        add.push([x, y]);
        add.push([xs, ys]);

        adds.push(add);

        // left web
        add = [];
        x = b_lft;
        y = t_btm_fl;

        xs = x;
        ys = y;

        add.push([xs, ys]);
        x += x1_lft;
        add.push([x, y]);
        x += x0_lft;
        y += h1;
        add.push([x, y]);
        if (cut === 0 || cut === 2) {
          y += t_top_fl / 2.0;
          add.push([x, y]);
          y -= t_top_fl / 2.0;
        }
        x -= x1_lft;
        add.push([x, y]);
        add.push([xs, ys]);

        adds.push(add);

        // right web
        add = [];
        x = w - b_rgt - x1_rgt;
        y = t_btm_fl;

        xs = x;
        ys = y;

        add.push([xs, ys]);
        x += x1_rgt;
        add.push([x, y]);
        x -= x0_rgt;
        y += h1;
        add.push([x, y]);
        x -= x1_rgt;
        if (cut === 0 || cut === 1) {
          y += t_top_fl / 2.0;
          add.push([x, y]);
          y -= t_top_fl / 2.0;
        }
        add.push([x, y]);
        add.push([xs, ys]);

        adds.push(add);
      }
      break;
    case ng.sections.SectionType.SECTION_TYPE_UX:
      if (sec.params?.type === "uxParams") {
        let h = sec.params.uxParams.height;
        let w = sec.params.uxParams.width;
        let t_fl = sec.params.uxParams.flangeThickness;
        let t_web = sec.params.uxParams.webThickness;

        // --------------- POLYLINE -----------------------------

        // lower flange
        let x = 0.0,
          y = 0.0;
        let xs = x,
          ys = y;

        perim.push([xs, ys]);
        x += w;
        perim.push([x, y]);
        y += t_fl;
        perim.push([x, y]);
        x -= w;
        perim.push([x, y]);
        perim.push([xs, ys]);

        // web
        add = [];
        x = 0;
        y = t_fl;

        xs = x;
        ys = y;

        add.push([xs, ys]);
        x += t_web;
        add.push([x, y]);
        y += h - 2 * t_fl;
        add.push([x, y]);
        x -= t_web;
        add.push([x, y]);
        add.push([xs, ys]);

        adds.push(add);

        // top flange
        add = [];
        x = 0;
        y = h - t_fl;

        xs = x;
        ys = y;

        add.push([xs, ys]);
        x += w;
        add.push([x, y]);
        y += t_fl;
        add.push([x, y]);
        x -= w;
        add.push([x, y]);
        add.push([xs, ys]);

        adds.push(add);
      }
      break;
    case ng.sections.SectionType.SECTION_TYPE_ZX:
      if (sec.params?.type === "zxParams") {
        let h = sec.params.zxParams.height;
        let w = sec.params.zxParams.width;
        let t_fl = sec.params.zxParams.flangeThickness;
        let t_web = sec.params.zxParams.webThickness;

        // --------------- POLYLINE -----------------------------

        // lower flange
        let x = w - t_web,
          y = 0.0;
        let xs = x,
          ys = y;

        perim.push([xs, ys]);
        x += w;
        perim.push([x, y]);
        y += t_fl;
        perim.push([x, y]);
        x -= w;
        perim.push([x, y]);
        perim.push([xs, ys]);

        // web
        add = [];
        x = w - t_web;
        y = t_fl;

        xs = x;
        ys = y;

        add.push([xs, ys]);
        x += t_web;
        add.push([x, y]);
        y += h - 2 * t_fl;
        add.push([x, y]);
        x -= t_web;
        add.push([x, y]);
        add.push([xs, ys]);

        adds.push(add);

        // top flange
        add = [];
        x = 0;
        y = h - t_fl;

        xs = x;
        ys = y;

        add.push([xs, ys]);
        x += w;
        add.push([x, y]);
        y += t_fl;
        add.push([x, y]);
        x -= w;
        add.push([x, y]);
        add.push([xs, ys]);

        adds.push(add);
      }
      break;

    default:
      break;
  }

  let pl: ng.geometry.IPoint2D[] = [];

  for (let i = 0; i < perim.length; i++) {
    let pnt: ng.geometry.Point2D = ng.geometry.Point2D.create({ x: perim[i][0], y: perim[i][1] });
    pl.push(pnt);
  }

  if (sec && sec.polylines && sec.polylines.perimeter) {
    sec.polylines.perimeter.points = pl;
  }

  let extra = [];
  for (let j = 0; j < adds.length; j++) {
    pl = [];
    for (let i = 0; i < adds[j].length; i++) {
      let pnt: ng.geometry.Point2D = ng.geometry.Point2D.create({ x: adds[j][i][0], y: adds[j][i][1] });
      pl.push(pnt);
    }

    if (sec && sec.polylines && sec.polylines.additionalPerimeters) {
      let add = ng.geometry.PolyLine2D.create({ points: pl });
      extra.push(add);
    }
  }

  if (sec && sec.polylines && sec.polylines.additionalPerimeters) {
    sec.polylines.additionalPerimeters = extra;
  }

  return sec;
};
